/**
 * 当前的hook包含了规则匹配、modal展示、问题单处理等逻辑
 * 后续车辆问题标签变多，可拆分成：
 * 1. engine.ts（负责规则匹配）
 * 2. presenters.ts（负责showModal函数的封装，根据不同场景展示不同modal）
 * 3. actions.ts（负责调用接口处理问题单的函数）
 * 4. 最后在这个hook里进行整合
 */

import { BtnStyleType, showModal } from '@/components/ModalWithAsyncLock';
import { RealTimeData } from '@/utils/types';
import { VehicleIssueTypeEnum } from '@/utils/enum';
import { VehicleIssueType } from '@/utils/types';
import { useCallback, useMemo } from 'react';
import { historyIssueOrdersApi } from '@/fetch/business/historyIssueOrders';
import { showErrorToast } from '@/utils/toast';
import { HttpStatusCode } from '@/fetch/core/constant';
import { generateTrackingId } from '..';
import useDelay from '@/utils/hooks/useDelay';

// 弹窗的按钮埋点id
enum TrackingId {
  cancelUseVehicleInRepairingOrder = 'cancelUseVehicleInRepairingOrder',
  acceptWaitToCheckOrdersAndUseVehicle = 'acceptWaitToCheckOrdersAndUseVehicle',
  notAcceptWaitToCheckOrdersAndUseVehicle = 'notAcceptWaitToCheckOrdersAndUseVehicle',
  cancelUseVehicleInWaitToCheckOrder = 'cancelUseVehicleInWaitToCheckOrder',
  cancelUseVehicleInStopOrder = 'cancelUseVehicleInStopOrder',
  endDecommissioningOrderAndUseVehicle = 'endDecommissioningOrderAndUseVehicle'
}

// 车辆问题校验上下文
interface ValidateVehicleHasIssueActionContext {
  /** 问题类型 */
  issueType: VehicleIssueType;
  /** 校验通过的外部函数（进入目标页面） */
  passValidationCallbackFunc: () => void | Promise<void>;
  /** 若传递此函数，说明需要二次校验，某个状态结束后需要调用单车实时信息接口进入二次校验 */
  getVehicleRealtimeInfo?: () => Promise<
    HTTPResponse<RealTimeData> | undefined
  >;
  /** 在校验失败或用户取消时的回调函数，用于回退 UI 或清理状态 */
  onCancelOrFailValidation?: () => void;
  /** 校验时机，创建任务时校验还是发车时校验，用于埋点区分 */
  verificationTiming?: 'createTask' | 'vehicleDepart';
}

// 暴露给外部的校验方法的函数类型定义
export type ValidateVehicleHasNoIssueFunc = (
  params: ValidateVehicleHasIssueActionContext
) => boolean;

// 校验规则列表中，每个校验函数action的入参定义
interface VehicleIssueRule {
  /** 问题类型名称，用于区分不同的校验逻辑 */
  name: string;
  /** 校验条件，若返回 true 则执行该校验 */
  when: (issueType: VehicleIssueType) => boolean;
  /** 车辆问题校验的领域动作，可能会有不同的交互形式（弹窗、接口调用等），通过传入的上下文参数进行处理 */
  run: (
    ctx: ValidateVehicleHasIssueActionContext,
    validate: ValidateVehicleHasNoIssueFunc
  ) => void;
}

// 本hook的入参类型定义
interface useVehicleIssueValidatorProps {
  /** 车牌号 */
  vehicleName: string | null;
  /** 车辆问题校验的来源页面，用于埋点区分 */
  fromPage: string;
  /** 业务类型，用于埋点区分（点对点接驳/蛛网） */
  businessType?: string;
}

/**
 * 车辆异常反馈单校验 hook
 * 在 创建任务 和 发车 之前都会校验
 *
 * @param {useVehicleIssueValidatorProps} props
 * @return {*}
 */
function useVehicleIssueValidator(props: useVehicleIssueValidatorProps) {
  const { vehicleName, fromPage, businessType } = props;

  // 拿到延迟方法（用于停用工单结束后等待车辆状态更新再进行二次校验）
  const { delay } = useDelay();

  /**
   * 处理车辆的‘维修中’状态
   */
  const showVehicleRepairingModal = useCallback(
    (callbackConfig: ValidateVehicleHasIssueActionContext) => {
      showModal({
        content: '该车辆有影响运营的故障，无法使用，请选择其他车辆。',
        footer: [
          {
            type: BtnStyleType.primary,
            btnText: '换一台车',
            autoClose: true,
            onClick: () => {
              callbackConfig.onCancelOrFailValidation &&
                callbackConfig.onCancelOrFailValidation();
            },
            trackingID: generateTrackingId(
              fromPage,
              TrackingId.cancelUseVehicleInRepairingOrder
            ),
            trackingParams: {
              vehicleName,
              businessType,
              verificationTiming: callbackConfig.verificationTiming
            }
          }
        ]
      });
    },
    [fromPage, vehicleName, businessType]
  );

  /**
   * 验收通过该车下所有待验收的维修单
   */
  const acceptVehicleWaitToCheckRepairOrders = useCallback(
    async (params: {
      passValidationCallbackFunc: ValidateVehicleHasIssueActionContext['passValidationCallbackFunc'];
      onCancelOrFailValidation?: ValidateVehicleHasIssueActionContext['onCancelOrFailValidation'];
    }) => {
      const { passValidationCallbackFunc, onCancelOrFailValidation } = params;
      try {
        if (!vehicleName) {
          throw new Error('暂未获取车号，请稍后再试');
        }
        const res = await historyIssueOrdersApi.checkVehicleAllRequire({
          vehicleName: vehicleName
        });
        if (res && res.code === HttpStatusCode.Success) {
          await passValidationCallbackFunc?.();
          return true;
        } else {
          throw new Error('操作失败，请稍后再试');
        }
      } catch (error: any) {
        showErrorToast((error && error.message) || '操作失败');
        try {
          onCancelOrFailValidation && onCancelOrFailValidation();
        } catch (_) {}
        return false;
      }
    },
    [vehicleName]
  );

  /**
   * 处理车辆的‘待验收’状态
   */
  const showVehicleWaitToCheckRepairModal = useCallback(
    (callbackConfig: ValidateVehicleHasIssueActionContext) => {
      const {
        passValidationCallbackFunc,
        onCancelOrFailValidation,
        verificationTiming
      } = callbackConfig;

      showModal({
        content: '车辆当前有重要零部件已经维修完成，请您确认是否验收通过？',
        footerLayout: 'column',
        footer: [
          {
            type: BtnStyleType.primary,
            btnText: '验收通过并用车',
            onClick: async (closeModal) => {
              const ok = await acceptVehicleWaitToCheckRepairOrders({
                passValidationCallbackFunc,
                onCancelOrFailValidation
              });
              ok && closeModal && closeModal();
            },
            trackingID: generateTrackingId(
              fromPage,
              TrackingId.acceptWaitToCheckOrdersAndUseVehicle
            ),
            trackingParams: {
              vehicleName,
              businessType,
              verificationTiming: verificationTiming
            }
          },
          {
            type: BtnStyleType.warn,
            btnText: '暂不验收，先用车',
            onClick: async (closeModal) => {
              try {
                await passValidationCallbackFunc();
                closeModal && closeModal();
              } catch (error: any) {
                showErrorToast(error?.message || '操作失败');
                onCancelOrFailValidation && onCancelOrFailValidation();
              }
            },
            trackingID: generateTrackingId(
              fromPage,
              TrackingId.notAcceptWaitToCheckOrdersAndUseVehicle
            ),
            trackingParams: {
              vehicleName,
              businessType,
              verificationTiming: verificationTiming
            }
          },
          {
            type: BtnStyleType.default,
            btnText: '取消用车',
            autoClose: true,
            onClick: () => {
              onCancelOrFailValidation && onCancelOrFailValidation();
            },
            trackingID: generateTrackingId(
              fromPage,
              TrackingId.cancelUseVehicleInWaitToCheckOrder
            ),
            trackingParams: {
              vehicleName,
              businessType,
              verificationTiming: verificationTiming
            }
          }
        ]
      });
    },
    [acceptVehicleWaitToCheckRepairOrders, fromPage, vehicleName, businessType]
  );

  /**
   * 结束车辆停用状态（该车下所有的停用工单）
   * 仅负责调用接口结束工单本身
   */
  const endVehicleInfrastratureOrExtremeWeatherIssue = useCallback(async () => {
    try {
      if (!vehicleName) {
        throw new Error('暂未获取车号，请稍后再试');
      }

      // 调用接口结束该车下的停用工单
      const res = await historyIssueOrdersApi.endException({
        vehicleName: vehicleName
      });

      // 失败则抛出错误，外部函数会捕获错误
      if (!res || res.code !== HttpStatusCode.Success) {
        throw new Error(res?.message || '操作失败，请稍后再试');
      }
    } catch (error: any) {
      showErrorToast((error && error.message) || '操作失败');
      throw error;
    }
  }, [vehicleName]);

  /**
   * 停用工单结束后的二次校验流程（仅仅负责二次校验）
   * 等待状态同步 -> 拉取最新的单车页轮询接口查询车辆issueType -> 递归校验 -> 通过则继续
   */
  const revalidateAfterStopOrderEnded = useCallback(
    async (
      callbackConfig: ValidateVehicleHasIssueActionContext,
      validate: ValidateVehicleHasNoIssueFunc
    ) => {
      const {
        passValidationCallbackFunc,
        getVehicleRealtimeInfo,
        onCancelOrFailValidation,
        verificationTiming
      } = callbackConfig;

      // 需要二次校验车辆是否有问题，拿到车辆实时信息后再次进入校验
      try {
        if (getVehicleRealtimeInfo && validate) {
          // 等待100ms，等待车辆实时信息更新，因为后端结束停用工单为异步，所以需要等待
          const ok = await delay(100);
          // delay被取消/组件卸载时，直接return
          if (!ok) return;

          // 获取车辆实时信息
          const res = await getVehicleRealtimeInfo();
          if (res && res.code === HttpStatusCode.Success && res.data) {
            // 拿到实时信息之后再次校验车辆是否有问题
            const newIssueType = res.data.warnType;
            const hasNoIssue = validate({
              issueType: newIssueType,
              passValidationCallbackFunc,
              getVehicleRealtimeInfo,
              onCancelOrFailValidation,
              verificationTiming
            });
            // 如果二次校验通过，则继续后续流程（如发车、导航等）
            if (hasNoIssue) {
              await passValidationCallbackFunc();
            }
          } else {
            throw new Error(res?.message || '操作失败，请稍后再试');
          }
          return;
        }

        // 不需要二次校验，直接通过校验
        await passValidationCallbackFunc();
      } catch (error: any) {
        showErrorToast((error && error.message) || '操作失败');
        try {
          onCancelOrFailValidation && onCancelOrFailValidation();
        } catch (_) {}
      }
    },
    [delay]
  );

  /**
   * STOP 弹窗“已结束，立即用车”的点击处理：结束工单成功即关闭弹窗，二次校验异步继续。
   */
  const onStopOrderEndedAndUseVehicleClick = useCallback(
    async (args: {
      callbackConfig: ValidateVehicleHasIssueActionContext;
      validate: ValidateVehicleHasNoIssueFunc;
      closeModal?: () => void;
    }) => {
      const { callbackConfig, validate, closeModal } = args;

      // 调用接口结束工单
      try {
        await endVehicleInfrastratureOrExtremeWeatherIssue();
      } catch (_) {
        // 失败则提示错误，且直接return，不进行二次校验，不关闭弹窗
        try {
          callbackConfig.onCancelOrFailValidation &&
            callbackConfig.onCancelOrFailValidation();
        } catch (_) {}
        return;
      }

      // 成功则继续进行二次校验，二次校验的结果会决定是否进入下一步（如发车、导航等）
      await revalidateAfterStopOrderEnded(callbackConfig, validate);

      // 关闭弹窗
      try {
        closeModal && closeModal();
      } catch (_) {}
    },
    [
      endVehicleInfrastratureOrExtremeWeatherIssue,
      revalidateAfterStopOrderEnded
    ]
  );

  /**
   * 处理车辆的‘停用中’状态（需要二次校验）
   */
  const showVehicleInfrastratureOrExtremeWeatherModal = useCallback(
    (
      callbackConfig: ValidateVehicleHasIssueActionContext,
      validate: ValidateVehicleHasNoIssueFunc
    ) => {
      showModal({
        content: '请问当前极端天气或道路维修等影响车辆跑行的事件是否已结束？',
        footer: [
          {
            type: BtnStyleType.warn,
            btnText: '未结束，暂不用车',
            autoClose: true,
            onClick: () => {
              callbackConfig.onCancelOrFailValidation &&
                callbackConfig.onCancelOrFailValidation();
            },
            trackingID: generateTrackingId(
              fromPage,
              TrackingId.cancelUseVehicleInStopOrder
            ),
            trackingParams: {
              vehicleName,
              businessType,
              verificationTiming: callbackConfig.verificationTiming
            }
          },
          {
            type: BtnStyleType.primary,
            btnText: '已结束，立即用车',
            onClick: (closeModal) =>
              onStopOrderEndedAndUseVehicleClick({
                callbackConfig,
                validate,
                closeModal
              }),
            trackingID: generateTrackingId(
              fromPage,
              TrackingId.endDecommissioningOrderAndUseVehicle
            ),
            trackingParams: {
              vehicleName,
              businessType,
              verificationTiming: callbackConfig.verificationTiming
            }
          }
        ]
      });
    },
    [onStopOrderEndedAndUseVehicleClick, fromPage, vehicleName, businessType]
  );

  /**
   * 车辆有问题的校验列表
   * 有顺序要求，前面的项先校验（校验优先级：维修中 > 停用中 > 待验收）
   * 停用中的车辆需要二次校验，二次校验内容是：车辆是否处于“待验收”状态
   */
  const rules = useMemo<VehicleIssueRule[]>(() => {
    return [
      // 维修中
      {
        name: 'REPAIR',
        when: (issueType: VehicleIssueType) =>
          issueType === VehicleIssueTypeEnum.REPAIR,
        run: (ctx) => showVehicleRepairingModal(ctx)
      },
      // 停用中
      {
        name: 'STOP',
        when: (issueType: VehicleIssueType) =>
          issueType === VehicleIssueTypeEnum.STOP,
        run: (ctx, validate) =>
          showVehicleInfrastratureOrExtremeWeatherModal(ctx, validate)
      },
      // 待验收
      {
        name: 'TO_DO_CHECK',
        when: (issueType: VehicleIssueType) =>
          issueType === VehicleIssueTypeEnum.TO_DO_CHECK,
        run: (ctx) => showVehicleWaitToCheckRepairModal(ctx)
      }
    ];
  }, [
    showVehicleRepairingModal,
    showVehicleInfrastratureOrExtremeWeatherModal,
    showVehicleWaitToCheckRepairModal
  ]);

  /**
   * 校验车辆是否有问题
   * @param {ValidateVehicleHasIssueActionContext} params
   * @return {*}
   */
  const validateVehicleHasNoIssue: ValidateVehicleHasNoIssueFunc = useCallback(
    (params: ValidateVehicleHasIssueActionContext) => {
      // 获得当前的车辆问题类型
      const issueType = params.issueType;
      // 根据车辆问题类型找到第一个满足条件的校验规则
      const validator = rules.find((rule) => rule.when(issueType));
      // 如果没有匹配的规则，说明车辆没有问题，直接通过校验
      if (!validator) return true;
      // 如果有匹配的规则，则执行对应的校验逻辑
      validator.run(params, validateVehicleHasNoIssue);
      // 同步返回false，表示当前车辆有问题，无法通过校验，后续的逻辑（如发车、导航等）应该停止
      // 等待用户在校验的交互中进行下一步操作（如验收通过、结束停用等）
      return false;
    },
    [rules]
  );

  // 对外暴露校验方法
  return useMemo(
    () => ({
      validateVehicleHasNoIssue
    }),
    [validateVehicleHasNoIssue]
  );
}

export default useVehicleIssueValidator;
